/**
 * ContactFormCard - Contact form with validation and submission.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitContact } from '../../lib/api';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

export interface ContactFormCardProps {
  variant?: string;
  title?: string;
  subtitle?: string;
  successMessage?: string;
  showPhone?: boolean;
  showSubject?: boolean;
  fields?: unknown[];
  className?: string;
}

const ContactFormCard: React.FC<ContactFormCardProps> = ({
  title = 'Get in Touch',
  subtitle = "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
  successMessage = "Thank you! We'll be in touch soon.",
  showPhone = true,
  showSubject = false,
  className = '',
}) => {
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [serverError, setServerError] = useState('');

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim() || form.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('loading');
    setServerError('');

    try {
      await submitContact(form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setServerError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className={`form-card ${className}`} role="region" aria-label="Contact form">
        <div style={{ textAlign: 'center', padding: 'var(--spacing-8)' }}>
          <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--spacing-4)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 style={{ marginBottom: 'var(--spacing-2)', color: 'var(--color-text)' }}>Message Sent!</h3>
          <p style={{ color: 'var(--color-text-muted)' }}>{successMessage}</p>
          <button className="btn btn-secondary" style={{ marginTop: 'var(--spacing-4)' }} onClick={() => setStatus('idle')}>
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`form-card ${className}`} role="region" aria-label="Contact form">
      {title && <h2 style={{ marginBottom: 'var(--spacing-2)', color: 'var(--color-text)' }}>{title}</h2>}
      {subtitle && <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-6)' }}>{subtitle}</p>}

      <form onSubmit={handleSubmit} noValidate aria-label="Contact form">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
          <div className="form-field">
            <label htmlFor="contact-name" className="form-label">Full Name *</label>
            <input
              id="contact-name"
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className={`form-input ${errors.name ? 'error' : ''}`}
              placeholder="Jane Doe"
              required
              aria-describedby={errors.name ? 'name-error' : undefined}
              aria-invalid={!!errors.name}
            />
            {errors.name && <span id="name-error" className="form-error" role="alert">{errors.name}</span>}
          </div>

          <div className="form-field">
            <label htmlFor="contact-email" className="form-label">Email Address *</label>
            <input
              id="contact-email"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="jane@example.com"
              required
              aria-describedby={errors.email ? 'email-error' : undefined}
              aria-invalid={!!errors.email}
            />
            {errors.email && <span id="email-error" className="form-error" role="alert">{errors.email}</span>}
          </div>
        </div>

        {showPhone && (
          <div className="form-field" style={{ marginBottom: 'var(--spacing-4)' }}>
            <label htmlFor="contact-phone" className="form-label">Phone Number</label>
            <input
              id="contact-phone"
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="form-input"
              placeholder="+1 (555) 000-0000"
            />
          </div>
        )}

        {showSubject && (
          <div className="form-field" style={{ marginBottom: 'var(--spacing-4)' }}>
            <label htmlFor="contact-subject" className="form-label">Subject</label>
            <input
              id="contact-subject"
              type="text"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              className="form-input"
              placeholder="How can we help?"
            />
          </div>
        )}

        <div className="form-field" style={{ marginBottom: 'var(--spacing-6)' }}>
          <label htmlFor="contact-message" className="form-label">Message *</label>
          <textarea
            id="contact-message"
            name="message"
            value={form.message}
            onChange={handleChange}
            className={`form-input ${errors.message ? 'error' : ''}`}
            placeholder="Tell us about your project or question..."
            rows={5}
            required
            aria-describedby={errors.message ? 'message-error' : undefined}
            aria-invalid={!!errors.message}
          />
          {errors.message && <span id="message-error" className="form-error" role="alert">{errors.message}</span>}
        </div>

        {serverError && (
          <div role="alert" style={{ backgroundColor: 'var(--color-error-light)', color: 'var(--color-error)', padding: 'var(--spacing-3) var(--spacing-4)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
            {serverError}
          </div>
        )}

        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-4)', lineHeight: 'var(--line-height-relaxed)' }}>
          By submitting this form, you agree that we may store and use the information you provide to respond to your
          inquiry, as described in our <Link to="/privacy" style={{ color: 'var(--color-primary)' }}>Privacy Policy</Link> and{' '}
          <Link to="/terms" style={{ color: 'var(--color-primary)' }}>Terms of Service</Link>.
        </p>

        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={status === 'loading'}
          aria-busy={status === 'loading'}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {status === 'loading' ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

export default ContactFormCard;
